import {
  Text,
} from '@chakra-ui/react';
import {
  useQuartelyRoadMaintenanceCost,
} from '@src/modules/performance/context/reports-api-hooks';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import LineBarChart from '@src/modules/performance/components/charts/LineBarChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Maintenance Delivery',
  content: [
    {
      title: 'Quarterly road maintenance cost ($)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>The NLTP-to-date $ totals of prorated annual approved and claimed road maintenance costs, totalled at the end of each quarter within the current 2024-27 NLTP.</Text>
    <Text fontSize="inherit" mb={2}>The road maintenance costs include State Highways Operations, State Highways Pothole Prevention, Local Road Operations and Local Road Pothole Prevention activity classes, but exclude Emergency Works (EW) and North Island Weather Events (NIWE). The amounts include both the NLTF portion and TLA local share. The costs do not include non-NLTF works that are funded by the TLA/s.</Text>
    <Text fontSize="inherit" mb={2}>The quarterly Local Road YTD Approved totals are calculated by NZTA using an Expenditure Seasonality table for Local Road Operations and for Local Road Pothole Prevention.</Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Approved and claimed road maintenance costs sourced from NZTA.
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryQuarterlyCosts() {
  const { data: quarterlyRoadMaintenanceCost, isLoading } = useQuartelyRoadMaintenanceCost();

  // Quarterly Road Maintenance Cost Chart
  const quarterlyRoadMaintenanceCostCategories = [...new Set(quarterlyRoadMaintenanceCost?.map((item) => item.FormattedQuarter))];
  const quarterlyRoadMaintenanceCostData = [{
    name: 'Variance %',
    type: 'line',
    data: quarterlyRoadMaintenanceCostCategories.map((quarter) => ({
      x: quarter,
      y: quarterlyRoadMaintenanceCost?.find((item) => item.FormattedQuarter === quarter)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Approved',
    type: 'bar',
    data: quarterlyRoadMaintenanceCostCategories.map((quarter) => ({
      x: quarter,
      y: quarterlyRoadMaintenanceCost?.find((item) => item.FormattedQuarter === quarter)?.Forecast || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Claimed',
    type: 'bar',
    data: quarterlyRoadMaintenanceCostCategories.map((quarter) => ({
      x: quarter,
      y: quarterlyRoadMaintenanceCost?.find((item) => item.FormattedQuarter === quarter)?.Achieved || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  return (
    <ReportSection
      title="Road Maintenance Delivery"
      isLoading={isLoading}
      lockYear={quarterlyRoadMaintenanceCost?.[0]?.LockYear}
      quarter={quarterlyRoadMaintenanceCost?.at(-1)?.Quarter}
      quarterFormat="ltp"
      informationSource={informationSource}
    >
      <LineBarChart
        title="Quarterly road maintenance cost ($)"
        categories={quarterlyRoadMaintenanceCostCategories}
        series={quarterlyRoadMaintenanceCostData}
        width="280"
        height="280"
        leftAxisLabel=""
        rightAxisLabel="Variance %"
        barSeriesName="Approved"
        lineSeriesName="Variance %"
        barSeriesFormatter={(y) => {
          if (typeof y !== 'undefined' && y !== null) {
            return `$${(y / 1_000_000).toFixed(1)}M`;
          }
          return y;
        }}
        lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
      />
      <GroupedRegionMessage
        rcaIds={[1014, 1016]}
        roadTypes={['shlr', 'sh']}
        messagePrefix="Combined results for"
        messageSuffix="for State Highways and All Roads"
      />
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryQuarterlyCosts;
